import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { roomBpmByRunTypeServices } from 'services/ReferenceScreenServices/roomBpmByRunTypeServices';
import {
  RoomBpmByRunType,
  RoomBpmByRunTypePayload
} from 'Interfaces/ReferenceInterfaces/RoomBpmByRunTypeInterface';

export class RoomBpmByRunTypeStore {
  roomBpmByRunTypes: RoomBpmByRunType[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedRoomBpmByRunType: RoomBpmByRunType | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchRoomBpmByRunTypes = async (page = 1, pageSize = 100) => {
    this.isLoading = true;
    try {
      const response = await roomBpmByRunTypeServices.getAllRoomBpmByRunTypes(page, pageSize);
      runInAction(() => {
        this.roomBpmByRunTypes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching room bpm by run types');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollRoomBpmByRunTypes = async (page = 1, pageSize = 100) => {
    this.isPolling = true;
    try {
      const response = await roomBpmByRunTypeServices.getAllRoomBpmByRunTypes(page, pageSize);
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.roomBpmByRunTypes) !== JSON.stringify(response.data)) {
          this.roomBpmByRunTypes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if(this.environment === 'development') {
        console.error('Error polling room bpm by run types:', error);
      }
      toast.error('Error polling room bpm by run types!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 100) => {
    await this.fetchRoomBpmByRunTypes(page, pageSize);
  };

  createRoomBpmByRunType = async (
    roomBpmByRunTypeData: RoomBpmByRunTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await roomBpmByRunTypeServices.createRoomBpmByRunType(roomBpmByRunTypeData);
      if (response.status) {
        toast.success('Room BPM by run type created successfully');
        await this.fetchRoomBpmByRunTypes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create room BPM by run type');
        return false;
      }
    } catch (error) {
      toast.error('Error creating room BPM by run type');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateRoomBpmByRunType = async (
    id: number,
    roomBpmByRunTypeData: RoomBpmByRunTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await roomBpmByRunTypeServices.updateRoomBpmByRunType(
        id,
        roomBpmByRunTypeData
      );
      if (response.status) {
        toast.success('Room BPM by run type updated successfully');
        await this.fetchRoomBpmByRunTypes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update room BPM by run type');
        return false;
      }
    } catch (error) {
      toast.error('Error updating room BPM by run type');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteRoomBpmByRunType = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await roomBpmByRunTypeServices.deleteRoomBpmByRunType(id);
      if (response.status) {
        toast.success('Room BPM by Run Type deleted successfully');

        const isLastItemOnPage = this.roomBpmByRunTypes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchRoomBpmByRunTypes(this.pagination.currentPage - 1);
        } else {
          await this.fetchRoomBpmByRunTypes(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete Room BPM by Run Type');
      }
    } catch (error) {
      toast.error('Error deleting Room BPM by Run Type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedRoomBpmByRunType = (roomBpmByRunType: RoomBpmByRunType | null) => {
    this.selectedRoomBpmByRunType = roomBpmByRunType;
  };
}

export const roomBpmByRunTypeStore = new RoomBpmByRunTypeStore();
